(() => {
  'use strict';

  // Temporary diagnostic bridge. It stops collecting after the review window automatically.
  const DIAGNOSTIC_END = Date.parse('2026-08-13T22:00:00Z');
  const MAX_SIGNATURES = 3;
  if (Date.now() > DIAGNOSTIC_END || window.__drevosJsErrorFingerprint) return;

  const state = {
    expiresAt: DIAGNOSTIC_END,
    signatures: [],
    sent: 0,
    upgraded: false,
    flushScheduled: false
  };
  const seen = new Set();
  window.__drevosJsErrorFingerprint = state;

  const scrub = (value) => {
    let text = '';
    try {
      text = String(value || '');
    } catch (error) {
      text = 'unreadable_error';
    }
    return text
      .replace(/https?:\/\/[^\s)'"\]]+/gi, '[url]')
      .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, '[email]')
      .replace(/\b[0-9a-f]{8}-[0-9a-f-]{27,}\b/gi, '[uuid]')
      .replace(/\+?\d[\d\s().-]{7,}\d/g, '[phone]')
      .replace(/\b\d{5,}\b/g, '[number]')
      .replace(/(["'])[^"'\n]{24,}\1/g, '$1[value]$1')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 140);
  };

  const assetName = (value) => {
    if (!value) return '';
    const source = String(value);
    if (/^(?:chrome|moz|safari)-extension:/i.test(source)) return '';
    try {
      const parsed = new URL(source, window.location.origin);
      return scrub(parsed.pathname.split('/').pop() || '').slice(0, 64);
    } catch (error) {
      return scrub(source.split(/[?#]/)[0].split('/').pop() || '').slice(0, 64);
    }
  };

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return (result >>> 0).toString(16).padStart(8, '0');
  };

  const activeExternalLoads = () => {
    const loads = window.__drevosExternalLoads;
    if (!loads || typeof loads !== 'object') return '';
    const now = Date.now();
    return Object.keys(loads).filter((name) => Number(loads[name]) > now).sort().join('+');
  };

  const analyticsAllowed = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    try {
      return privacy.analyticsProcessingAllowed() === true;
    } catch (error) {
      return false;
    }
  };

  const firstStackLocation = (error) => {
    if (!error || typeof error.stack !== 'string') return null;
    const lines = error.stack.split('\n');
    for (let index = 0; index < lines.length; index += 1) {
      const match = lines[index].match(/(https?:\/\/[^\s)]+?)(?::(\d+))(?::(\d+))(?:\)?$|\s)/i);
      if (match && !/^(?:chrome|moz|safari)-extension:/i.test(match[1])) {
        return { source: match[1], line: Number(match[2]) || 0, column: Number(match[3]) || 0 };
      }
    }
    return null;
  };

  const flush = () => {
    state.flushScheduled = false;
    if (!analyticsAllowed() || typeof window.clarity !== 'function') return;

    try {
      while (state.sent < state.signatures.length) {
        window.clarity('set', 'js_error_sig', state.signatures[state.sent]);
        window.clarity('event', 'js_error_captured');
        state.sent += 1;
      }
      if (state.sent && !state.upgraded) {
        state.upgraded = true;
        window.clarity('upgrade', 'js-error');
      }
    } catch (error) {
      // Diagnostics must never interfere with storefront behavior.
    }
  };

  const scheduleFlush = () => {
    if (state.flushScheduled) return;
    state.flushScheduled = true;
    window.setTimeout(flush, 0);
  };

  const record = (kind, error, message, source, line, column) => {
    if (state.signatures.length >= MAX_SIGNATURES) return;
    const stackLocation = firstStackLocation(error);
    const errorName = scrub(error && error.name ? error.name : 'Error').slice(0, 36) || 'Error';
    const cleanMessage = scrub(message || (error && error.message) || 'unknown_error') || 'unknown_error';
    const file = assetName(source || (error && error.fileName) || (stackLocation && stackLocation.source));
    const phase = activeExternalLoads();
    const generic = /^script error\.?$/i.test(cleanMessage);

    // A generic cross-origin error has no diagnostic value unless it overlaps a known external load.
    if (generic && !phase) return;
    if (!file && !phase && cleanMessage === 'unknown_error') return;

    const location = `${Number(line) || (stackLocation && stackLocation.line) || 0}:${Number(column) || (stackLocation && stackLocation.column) || 0}`;
    const payload = [kind, errorName, file || phase || 'unknown', location, cleanMessage, phase]
      .filter(Boolean)
      .join('|');
    const signature = `${hash(payload)}|${payload}`.slice(0, 240);
    if (seen.has(signature)) return;
    seen.add(signature);
    state.signatures.push(signature);
    scheduleFlush();
    [1000, 3000, 8000].forEach((delay) => window.setTimeout(flush, delay));
  };

  window.addEventListener('error', (event) => {
    const source = event.filename || '';
    if (/^(?:chrome|moz|safari)-extension:/i.test(source)) return;
    record('error', event.error, event.message, source, event.lineno, event.colno);
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : null;
    record('rejection', error, error ? error.message : reason, error && error.fileName, 0, 0);
  });

  document.addEventListener('visitorConsentCollected', scheduleFlush);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', scheduleFlush);
  window.addEventListener('load', scheduleFlush, { once: true });
})();
